import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { CARRIERS, CARRIER_LOGOS, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Companies() {
  const { t } = useLang();
  usePageMeta(t("meta.companies")[0], t("meta.companies")[1]);
  const logoNames = new Set(CARRIER_LOGOS.map((c) => c.match));
  const textCarriers = CARRIERS.filter((name) => !logoNames.has(name));
  return (
    <>
      <PageHero
        overline={t("companiesPage.heroOverline")}
        title={t("companiesPage.heroTitle")}
        sub={t("companiesPage.heroSub")}
        testId="companies"
      />

      <Marquee />

      <section className="bg-white py-16 sm:py-24" aria-labelledby="carriers-heading" data-testid="carriers-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 id="carriers-heading" className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {t("companiesPage.heading")}
                </h2>
                <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-600">
                  <BadgeCheck className="h-4 w-4 text-[#D97706]" aria-hidden="true" /> {t("companiesPage.amBest")}
                </p>
              </div>
              <p className="max-w-sm text-xs leading-relaxed text-slate-500">
                {t("companiesPage.disclaimer")}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="carrier-logos-grid">
            {CARRIER_LOGOS.map((carrier, i) => (
              <Reveal key={carrier.name} delay={i % 4}>
                <div
                  className="flex h-32 items-center justify-center border border-[#0F4C5C]/10 bg-white p-5 shadow-sm transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-[#0F4C5C]/30 hover:shadow-lg"
                  data-testid={`carrier-logo-${carrier.file}`}
                >
                  <img
                    src={`/assets/carriers/${carrier.file}`}
                    alt={`${carrier.name} logo`}
                    className="max-h-20 w-auto max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            ))}
          </div>

          {textCarriers.length > 0 && (
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="carriers-grid">
              {textCarriers.map((carrier, i) => (
                <Reveal key={carrier} delay={i % 5}>
                  <div
                    className="flex h-20 items-center justify-center border border-[#0F4C5C]/10 bg-[#F8FAFC] px-4 text-center transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-[#0F4C5C]/30 hover:bg-[#EEF6FC]"
                    data-testid={`carrier-${carrier.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <span className="font-heading text-sm font-bold tracking-wide text-[#1A365D]">{carrier}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <Reveal>
            <div className="mt-14 flex flex-col items-center justify-between gap-6 bg-[#EEF6FC] p-8 sm:flex-row" data-testid="companies-cta-band">
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900">{t("companiesPage.ctaTitle")}</h2>
                <p className="mt-2 max-w-xl text-sm text-slate-600">
                  {t("companiesPage.ctaSub")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/quote" className="inline-flex items-center gap-2 bg-[#0F4C5C] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#1A365D]" data-testid="companies-quote-btn">
                  {t("common.requestQuote")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <a href={SITE.phone1Href} className="inline-flex items-center gap-2 border-2 border-[#0F4C5C] px-6 py-3 text-sm font-bold uppercase tracking-wider text-[#0F4C5C] transition-colors duration-200 hover:bg-[#0F4C5C] hover:text-white" data-testid="companies-call-btn">
                  <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone1}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
